<template>
	<div :class="$style['cart']">
		<div
			ref="content"
			:class="[
				$style['collapsible__content'],
				{ [$style['collapsible__content--in-transition']]: inTransition }
			]"
		>
			<DashboardContainer
				:class="$style['cart__container']"
				style="height:auto;"
			>
				<CartInner
					v-if="expanded"
					@refetch="handleFetchCart(cart.id)"
				/>
			</DashboardContainer>
		</div>

		<DashboardContainer :class="$style['cart__container']">
			<div :class="$style['cart__container__content']">
				<div :class="$style['cart__info']">
					<FontAwesomeIcon
						:class="$style['cart__container__content__icon']"
						:icon="['fal', 'shopping-cart']"
						size="lg"
						@click="$emit('toggle')"
					/>

					<div :class="$style['cart__container__content__text']">
						<div :class="$style['cart__container__content__text__title']">
							<div :class="$style['cart__title-wrapper']">
								<span>{{ $t('cart-view.title') }}</span>
								<FontAwesomeIcon
									v-if="loading"
									:icon="['far', 'circle-notch']"
									:class="$style['cart__loader']"
								/>
								<span
									v-else-if="cart"
									:class="$style['cart__products-count']"
								>
									({{ cart.quantity }})
								</span>
							</div>

							<BaseIconButton
								:class="[
									$style['collapsible__icon'],
									{
										[$style['collapsible__icon--collapsed']]: !expanded,
										[$style['collapsible__icon--in-transition']]: inTransition
									}
								]"
								@click="toggle"
							>
								<FontAwesomeIcon :icon="['fas', 'caret-down']" />
							</BaseIconButton>
						</div>

						<span :class="$style['cart__container__content__text__caption']">
							<span v-if="cart && cart.totalPrice">
								{{ $t('cart-view.total') }}:
								{{ cart.totalPrice | currency($auth().currency) }}
							</span>
							<a
								:class="$style['link']"
								href="#"
								@click.prevent="expand"
							>
								{{ $t('cart-view.actions.expand') }}
							</a>
						</span>
					</div>

					<BaseIconButton
						v-show="$breakpoints.xs"
						:class="$style['cart__container__content__container-button']"
						@click="handleDeleteCart(cart.id)"
					>
						<FontAwesomeIcon :icon="['fal', 'trash']" />
					</BaseIconButton>
				</div>

				<div :class="$style['input-wrapper']">
					<BaseButton
						v-if="!$breakpoints.xs"
						:class="$style['cart__checkout-button']"
						:disabled="checkoutDisabled"
						:loading="requesting"
						type="button"
						@click="handleViewCartSummary"
					>
						{{ $t('cart-view.actions.continue') }}
					</BaseButton>

					<BaseIconButton
						v-if="$breakpoints.xs"
						:class="$style['cart__checkout-button-mobile']"
						:disabled="checkoutDisabled"
						primary
						:loading="requesting"
						type="button"
						@click="handleViewCartSummary"
					>
						<FontAwesomeIcon :icon="['fas', 'arrow-right']" />
					</BaseIconButton>
				</div>

				<template v-if="!$breakpoints.xs">
					<FontAwesomeIcon
						v-if="loading.deleteCart"
						:icon="['far', 'circle-notch']"
						:class="$style['cart__loader']"
					/>
					<BaseIconButton
						v-else
						:loading="isDeleting"
						:class="$style['cart__container__content__container-button']"
						@click="handleDeleteCart(cart.id)"
					>
						<FontAwesomeIcon :icon="['fal', 'trash']" />
					</BaseIconButton>
				</template>
			</div>
		</DashboardContainer>

		<AddOrder
			v-if="showAddOrderModal"
			:department="departmentId"
			:parts="cartProducts"
			:data="defaultOrderData"
			:reservation="form.orderType.id === 'reservation'"
			@modal-toggle="showAddOrderModal = !showAddOrderModal"
		/>
		<AddClient
			v-else-if="showAddClientModal"
			:department="departmentId"
			:client="null"
			:refetch="false"
			@modal-toggle="showAddClientModal = false"
			@client-added="updateClient"
		/>
		<CheckoutModal
			v-else-if="showCheckoutModal"
			:key="checkoutKey"
			:cart="cart"
			:oem-cart="oemCart"
			:order-type="form.orderType"
			:products="cartProducts"
			@refetch="handleFetchCart(cart.id)"
			@add-client="showAddClientModal = true"
			@continue="handleContinue"
			@modal-toggle="showCheckoutModal = !showCheckoutModal"
		/>
	</div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import DashboardContainer from '@/components/Layout/Dashboard/Container'
import AddClient from '@/views/Dashboard/Clients/components/AddClient'
import CartInner from './CartInner'
import CheckoutModal from './CheckoutModal'
import breakpoints from '@/mixins/breakpoints'
import collapsible from '@/mixins/collapsible'

export default {
	name: 'Cart',
	components: {
		AddClient,
		DashboardContainer,
		CartInner,
		CheckoutModal,
		AddOrder: defineAsyncComponent(() =>
			import('@/views/Dashboard/Orders/components/AddOrder')
		)
	},
	mixins: [breakpoints, collapsible],

	props: {
		loading: {
			type: Boolean,
			required: true
		},
		cart: {
			type: [Object, null],
			default: null
		},
		cartProducts: {
			type: [Array, null],
			default: null
		},
		fetchCart: {
			type: Function,
			required: true
		},
		deleteCart: {
			type: Function,
			required: true
		},
		updateCart: {
			type: Function,
			required: true
		}
	},

	data () {
		return {
			checkoutKey: 0,
			showCheckoutModal: false,
			showAddClientModal: false,
			showAddOrderModal: false,
			isDeleting: false,
			requesting: false,
			form: {
				orderType: {
					id: null
				}
			}
		}
	},

	computed: {
		defaultOrderData () {
			return {
				client: this.form?.client,
				clientType: {
					...this.form?.clientType,
					id: this.form?.client?.id ? 'individual' : null
				},
				deliveryType: {
					...this.form?.deliveryType,
					id: this.form?.deliveryType?.id === 'delivery'
				}
			}
		},

		checkoutDisabled () {
			return this.loading || !this.cart || !this.cart.quantity
		},

		oemCart () {
			return this.cartProducts && this.cartProducts.some(
				product => product.productType !== 'part'
			)
		},

		departmentId () {
			return this.$store.getters['Parts/part']
				? this.$store.getters['Parts/part'].department.id
				: this.$store.getters['Auth/department']
					? this.$store.getters['Auth/department'].id
					: null
		}
	},

	watch: {
		'loading.cartParts' (isLoading, wasLoading) {
			if (wasLoading && !isLoading && this.expanded) {
				this.$nextTick(() => {
					this.recalculateHeight()
				})
			}
		}
	},

	methods: {
		handleContinue (data) {
			this.form = { ...this.form, ...data }
			this.showAddOrderModal = true
		},

		async updateClient (client) {
			await this.updateCart({
				id: this.cart.id,
				clientId: client.id,
				deliveryType: this.cart.deliveryType,
				products: this.cartProducts.map(product => ({
					id: product.recarId,
					quantity: product.quantity,
					productType: product.productType
				}))
			})
			await this.handleFetchCart(this.cart.id)
			this.checkoutKey += 1
		},

		async handleViewCartSummary () {
			this.$validator.resume()
			try {
				this.requesting = true
				const valid = await this.$validator.validateAll()
				if (valid) {
					this.showCheckoutModal = true
					this.requesting = false
				} else {
					this.requesting = false
					return
				}
			} catch (e) {
				this.requesting = false
				this.$store.dispatch(
					'Notification/ADD_ERROR_NOTIFICATION',
					e
				)
			}
		},

		toggleModal () {
			this.showCheckoutModal = !this.showCheckoutModal
		},

		async handleFetchCart (cartId) {
			try {
				await this.fetchCart(cartId)
			} catch (_e) {
				// silent
			}
		},

		async handleDeleteCart (cartId) {
			try {
				this.isDeleting = true
				await this.deleteCart({ id: cartId })
			} catch (_e) {
				// silent
			} finally {
				this.isDeleting = false
			}
		}
	}
}
</script>

<style lang="scss" module>
.input {
    order: 1;

    @include media-breakpoint-up(lg) {
        order: 0;
        width: auto;
        min-width: 240px;
    }
}

.input-wrapper {
    display: flex;
    align-items: center;
    margin-left: auto;
    flex-shrink: 0;
    // keep old visual spacing by using inline margins on buttons
    gap: 0;
}

.collapsible {
    &__icon {
        font-size: 16px;
        cursor: pointer;
        transform: rotate(-180deg);
        color: getColor(icon);
        padding: calc($gutter / 2);
        border-radius: 4px;
        transition: transform 0.2s ease-in-out, color 0.15s ease, background-color 0.15s ease;

        &--collapsed {
            transform: rotate(0);
        }

        &--in-transition {
            transition: transform 0.2s ease-in-out, color 0.15s ease, background-color 0.15s ease;
        }
    }

    &__content {
        overflow: hidden;
        overflow-y: auto;
        background-color: getColor(surface-overlay);
        height: auto;
        max-height: 80vh;
        border-radius: 4px;
        border: 1px solid getColor(border);

        @include dark-mode-border-fix(border);

        &--in-transition {
            transition: height 0.2s ease-in-out;
            will-change: height;
        }

        &::-webkit-scrollbar {
            width: 8px;
        }

        &::-webkit-scrollbar-track {
            background: getColor(surface);
        }

        &::-webkit-scrollbar-thumb {
            background: getColor(border-secondary);
            border-radius: 4px;

            &:hover {
                background: getColor(primary);
            }
        }
    }
}

.cart {
    position: fixed;
    z-index: 1001;
    width: 100%;
    height: auto;
    background-color: getColor(surface);
    box-shadow: 0 -4px 16px getColor(shadow);
    border-top: 1px solid getColor(border);
    bottom: 0;
    padding-bottom: env(safe-area-inset-bottom);
    display: flex;
    flex-direction: column;
    justify-content: center;
    transition: transform 0.3s ease-in-out, box-shadow 0.2s ease;

    &--hidden {
        transform: translateY(100%);
    }

    &__loader {
        color: getColor(primary);
        margin-left: calc($gutter / 2);
        font-size: 20px;
        animation: rotator 1.4s linear infinite;
    }

    &__products-count {
        padding-left: calc($gutter / 2);
        color: getColor(sub-text);
        font-weight: 600;
    }

    &__title-wrapper {
        display: flex;
        align-items: center;
        gap: calc($gutter / 2);
    }

    &__info {
        display: flex;
        align-items: center;
        margin-right: auto;
        margin-bottom: 0;
        min-width: 0;
        flex: 1;
    }

    &__checkout-button {
        margin-left: 10px;
    }

    &__checkout-button-mobile {
        margin-right: 0;
    }

    &__container {
        height: 100%;
        padding: $gutter * 2;
        padding-bottom: $gutter;

        &__content {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: $gutter;

            &__icon {
                font-size: 30px;
                margin-right: 0;
                margin-top: 2px;
                cursor: pointer;
                color: getColor(icon);
                padding: calc($gutter / 2);
                border-radius: 4px;
                transition: color 0.15s ease, background-color 0.15s ease;

                &:hover {
                    background-color: getColor(hover-overlay);
                    color: getColor(icon-active);
                }

                &:active {
                    background-color: getColor(active-overlay);
                }
            }

            &__text {
                margin-left: $gutter * 1.5;
                display: flex;
                flex-direction: column;
                min-width: 0;

                .link {
                    color: getColor(primary);
                    text-decoration: none;
                    font-weight: 600;
                    transition: color 0.15s ease;

                    &:hover {
                        color: getColor(primary-hover);
                        text-decoration: underline;
                    }
                }

                &__title {
                    font-size: 17px;
                    font-weight: 700;
                    padding-bottom: 5px;
                    display: flex;
                    align-items: center;
                    color: getColor(text);
                }

                &__caption {
                    font-size: 12px;
                    color: getColor(sub-text);
                }
            }

            &__container-button {
                font-size: 17px;
                margin-left: auto;
            }
        }

        @include media-breakpoint-up(lg) {
            padding: $gutter * 2 $gutter * 4;
            margin: 0;
            padding-bottom: $gutter;

            &__info {
                margin-bottom: 0;
            }

            &__content {
                display: flex;
                align-items: center;
                gap: $gutter;
				flex-direction: row;

                &__text {
                    margin-left: 0;
                }

                &__container-button {
                    font-size: 17px;
                    margin-left: 0;
                }

                &__icon {
                    font-size: 35px;
                    margin-right: $gutter * 1.25;
                    margin-top: 2px;
                }
            }
        }
    }
}

@keyframes rotator {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(270deg);
    }
}
</style>
